/**
 * SMS Consent Block
 *
 * Renders a pre-checked, disabled checkbox with SMS policy text
 * and a clickable link that opens the SMS policy modal.
 *
 * Layout mirrors booking-v3: flex row with the checkbox icon on the left
 * and a separate Typography label on the right.
 */
import { memo, useCallback } from "react";
import { Link, makeStyles, Typography } from "@material-ui/core";
import { Checkbox, colors } from "@chhjit/react-components";

import { useSmsPolicy } from "../../../hooks/use-sms-ploicy";
import { useFieldConfig } from "../../../context/step-config-context";

const useStyles = makeStyles({
  smsPolicy: {
    display: "flex",
    marginLeft: 5,
    "& $checkboxLabel": {
      marginRight: 0,
    },
  },

  checkboxLabel: {},

  checkboxRoot: {},

  smsPolicyLink: {
    cursor: "pointer",
    fontSize: "inherit",
    fontFamily: "inherit",
    fontWeight: 400,
    lineHeight: "inherit",
    display: "inline-flex",
    alignItems: "baseline",
    background: "none",
    border: "none",
    padding: 0,
    marginTop: -3,
    color: colors.primary.dark,
    textDecoration: "underline",
  },
});

export const SmsConsentBlock = memo(() => {
  const styles = useStyles();
  const { getTextBlock } = useFieldConfig();
  const openSmsPolicy = useSmsPolicy({
    title: getTextBlock("smsPolicyTitle", ""),
    message: getTextBlock("smsPolicyBody", ""),
    closeButtonText: getTextBlock("smsPolicyCloseButton", ""),
  });

  const noop = useCallback(() => {}, []);

  return (
    <div className={styles.smsPolicy}>
      <Checkbox
        id="ob-sms-consent"
        name="smsConsent"
        variant="outline"
        checked
        disabled
        onChange={noop}
        className={styles.checkboxLabel}
        classes={{ root: styles.checkboxRoot }}
      />
      <Typography variant="subtitle1" component="span">
        {getTextBlock("smsConsentPrefix", "I agree to the use of")}{" "}
        <Link
          variant="subtitle1"
          onClick={openSmsPolicy}
          component="button"
          tabIndex={0}
          role="button"
          aria-label="Open SMS texts policy"
          className={styles.smsPolicyLink}
        >
          {getTextBlock("smsConsentLinkText", "SMS texts policy")}
        </Link>{" "}
        {getTextBlock(
          "smsConsentSuffix",
          "to communicate with me about my appointment. I understand that I can unsubscribe anytime",
        )}
      </Typography>
    </div>
  );
});
